/**
 * AdminManageScreensPage - [UC-063]
 * Admin page for managing screens in a project - add new screens and delete existing ones.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, Card } from '../components/ui';
import { api } from '../core/api-client';
import toast from 'react-hot-toast';

interface Screen {
  screenId: number;
  name: string;
  projectId: number;
  filePath: string;
}

interface ScreenToDelete extends Screen {
  inUse?: boolean;
}

export function AdminManageScreensPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [screens, setScreens] = useState<Screen[]>([]);
  const [loading, setLoading] = useState(true);
  const [newScreenName, setNewScreenName] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [screenToDelete, setScreenToDelete] = useState<ScreenToDelete | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const loadScreens = async () => {
      if (!projectId) return;

      try {
        setLoading(true);
        setErrorMessage('');
        const data = await api.get<{items: Screen[]}>(`/projects/${projectId}/screens`);
        setScreens(data.items || []);
      } catch (error: any) {
        console.error('Failed to load screens:', error);
        setErrorMessage(t('adminManageScreens.loadError'));
        toast.error(t('adminManageScreens.loadError'));
      } finally {
        setLoading(false);
      }
    };

    loadScreens();
  }, [projectId, t]);

  const handleAddScreen = async () => {
    if (!newScreenName.trim() || !projectId) return;

    try {
      setIsAdding(true);
      await api.post<Screen>(`/projects/${projectId}/screens`, {
        name: newScreenName.trim()
      });

      setNewScreenName('');
      toast.success(t('adminManageScreens.addSuccess'));

      // Refresh the screen list
      const data = await api.get<{items: Screen[]}>(`/projects/${projectId}/screens`);
      setScreens(data.items || []);
    } catch (error: any) {
      console.error('Failed to add screen:', error);
      toast.error(t('adminManageScreens.addError'));
    } finally {
      setIsAdding(false);
    }
  };

  const handleConfirmDelete = async (screen: Screen) => {
    try {
      // First get screen details to check if it exists
      await api.get<Screen>(`/screens/${screen.screenId}`);

      setScreenToDelete({ ...screen, inUse: false });
      setShowDeleteModal(true);
    } catch (error: any) {
      console.error('Failed to get screen details:', error);
      toast.error(t('adminManageScreens.screenNotFound'));
    }
  };

  const handleDeleteScreen = async () => {
    if (!screenToDelete) return;

    try {
      await api.delete(`/screens/${screenToDelete.screenId}`);

      setShowDeleteModal(false);
      setScreenToDelete(null);
      toast.success(t('adminManageScreens.deleteSuccess'));

      // Refresh the screen list
      const data = await api.get<{items: Screen[]}>(`/projects/${projectId}/screens`);
      setScreens(data.items || []);
    } catch (error: any) {
      console.error('Failed to delete screen:', error);

      if (error.status === 409) {
        // Screen is in use - show warning in modal
        setScreenToDelete(prev => prev ? { ...prev, inUse: true } : null);
      } else {
        setShowDeleteModal(false);
        setScreenToDelete(null);
        toast.error(t('adminManageScreens.deleteError'));
      }
    }
  };

  const handleForceDelete = async () => {
    if (!screenToDelete) return;

    try {
      // Force delete even if in use (this would be the confirmed deletion)
      await api.delete(`/screens/${screenToDelete.screenId}`);

      setShowDeleteModal(false);
      setScreenToDelete(null);
      toast.success(t('adminManageScreens.deleteSuccess'));

      // Refresh the screen list
      const data = await api.get<{items: Screen[]}>(`/projects/${projectId}/screens`);
      setScreens(data.items || []);
    } catch (error: any) {
      console.error('Failed to force delete screen:', error);
      setShowDeleteModal(false);
      setScreenToDelete(null);
      toast.error(t('adminManageScreens.deleteError'));
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setScreenToDelete(null);
  };

  const handleCancel = () => {
    navigate(`/admin/projects/${projectId}`);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      <div className="mb-5 sm:mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
          {t('adminManageScreens.title')}
        </h1>
        <p className="mt-1.5 text-base sm:text-sm text-gray-600">
          {t('adminManageScreens.subtitle')}
        </p>
      </div>

      {/* Add New Screen */}
      <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4 sm:p-5">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
          {t('adminManageScreens.addNewScreen')}
        </h2>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label htmlFor="newScreenName" className="block text-sm font-medium text-gray-600 mb-1.5">
              {t('adminManageScreens.screenNameLabel')}
            </label>
            <Input
              id="newScreenName"
              value={newScreenName}
              onChange={(e) => setNewScreenName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddScreen()}
              placeholder={t('adminManageScreens.screenNamePlaceholder')}
              className="w-full"
              disabled={isAdding}
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={handleAddScreen}
              disabled={!newScreenName.trim() || isAdding}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isAdding ? t('adminManageScreens.adding') : t('adminManageScreens.addScreen')}
            </Button>
          </div>
        </div>
      </div>

      {/* Screens Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {errorMessage && (
          <div className="p-4 bg-red-50 border-b border-red-200">
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {t('adminManageScreens.screenNameHeader')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {t('adminManageScreens.createdHeader')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {t('adminManageScreens.actionsHeader')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-gray-600">
                    {t('adminManageScreens.loading')}
                  </td>
                </tr>
              ) : screens.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-gray-600">
                    {t('adminManageScreens.noScreens')}
                  </td>
                </tr>
              ) : (
                screens.map((screen) => (
                  <tr key={screen.screenId} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-base sm:text-sm text-gray-900 font-medium">
                      {screen.name}
                    </td>
                    <td className="px-4 py-3 text-base sm:text-sm text-gray-600">
                      {t('adminManageScreens.createdRecently')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        onClick={() => handleConfirmDelete(screen)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        {t('adminManageScreens.delete')}
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex justify-end">
        <Button
          onClick={handleCancel}
          variant="outline"
          className="bg-white text-gray-900 border-gray-200 hover:bg-gray-50"
        >
          {t('adminManageScreens.cancel')}
        </Button>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && screenToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 max-w-sm sm:max-w-md w-full shadow-2xl mx-4">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {screenToDelete.inUse
                ? t('adminManageScreens.deleteInUseTitle')
                : t('adminManageScreens.deleteTitle')
              }
            </h2>

            <div className="mb-6">
              <p className="text-base sm:text-sm text-gray-600 mb-3">
                {t('adminManageScreens.deleteMessage', { name: screenToDelete.name })}
              </p>

              {screenToDelete.inUse && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700 font-medium">
                    {t('adminManageScreens.inUseWarning')}
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <Button
                onClick={handleCancelDelete}
                variant="outline"
                className="w-full sm:w-auto bg-white text-gray-900 border-gray-200 hover:bg-gray-50"
              >
                {t('adminManageScreens.cancelDelete')}
              </Button>
              <Button
                onClick={screenToDelete.inUse ? handleForceDelete : handleDeleteScreen}
                className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white"
              >
                {t('adminManageScreens.confirmDelete')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminManageScreensPage;